import React, { useState, useEffect } from 'react';
import { IonToast, IonIcon, IonBadge } from '@ionic/react';
import { wifi, wifiOutline } from 'ionicons/icons';
import './ConnectionStatus.css';

interface ConnectionStatusProps {
  showBadge?: boolean;
  showToast?: boolean;
}

const ConnectionStatus: React.FC<ConnectionStatusProps> = ({
  showBadge = true,
  showToast = true
}) => {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [showOfflineToast, setShowOfflineToast] = useState<boolean>(false);
  const [showOnlineToast, setShowOnlineToast] = useState<boolean>(false);
  const [showCachedDataToast, setShowCachedDataToast] = useState<boolean>(false);
  const [cachedDataInfo, setCachedDataInfo] = useState<{url: string, age: number}>({url: '', age: 0});
  const [initialLoad, setInitialLoad] = useState<boolean>(true);

  useEffect(() => {
    // Función para manejar cambios en la conexión
    const handleConnectionChange = (event: Event | CustomEvent) => {
      const online = navigator.onLine;

      // Solo mostrar toasts después de la carga inicial
      if (!initialLoad) {
        if (online) {
          setShowOnlineToast(true);
        } else {
          setShowOfflineToast(true);
        }
      } else {
        setInitialLoad(false);
      }

      setIsOnline(online);
    };

    // Escuchar eventos nativos
    window.addEventListener('online', handleConnectionChange);
    window.addEventListener('offline', handleConnectionChange);

    // Escuchar eventos personalizados del Service Worker
    window.addEventListener('connectionChange', (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail && typeof customEvent.detail.isOnline === 'boolean') {
        setIsOnline(customEvent.detail.isOnline);

        // Solo mostrar toasts después de la carga inicial
        if (!initialLoad) {
          if (customEvent.detail.isOnline) {
            setShowOnlineToast(true);
          } else {
            setShowOfflineToast(true);
          }
        } else {
          setInitialLoad(false);
        }
      }
    });

    // Escuchar mensajes del Service Worker sobre datos en caché
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'USING_CACHED_DATA') {
          console.log('Usando datos en caché:', event.data);
          setCachedDataInfo({
            url: event.data.url,
            age: event.data.age
          });
          setShowCachedDataToast(true);
        }
      });
    }

    // Verificar estado inicial
    setIsOnline(navigator.onLine);

    // Limpiar event listeners
    return () => {
      window.removeEventListener('online', handleConnectionChange);
      window.removeEventListener('offline', handleConnectionChange);
      window.removeEventListener('connectionChange', handleConnectionChange as EventListener);
    };
  }, [initialLoad]);

  return (
    <>
      {showBadge && (
        <div className={`connection-status-badge ${isOnline ? 'online' : 'offline'}`}>
          <IonBadge color={isOnline ? 'success' : 'warning'}>
            <IonIcon icon={isOnline ? wifi : wifiOutline} />
            {isOnline ? ' Online' : ' Offline'}
          </IonBadge>

          {/* Banner de modo offline que aparece cuando no hay conexión */}
          {!isOnline && (
            <div className="offline-banner">
              <IonIcon icon={wifiOutline} />
              <span>Modo sin conexión</span>
              <p>Algunas funciones no están disponibles</p>
              <button
                className="refresh-button"
                onClick={() => window.location.reload()}
              >
                Reintentar conexión
              </button>
            </div>
          )}
        </div>
      )}

      {showToast && (
        <>
          <IonToast
            isOpen={showOfflineToast}
            onDidDismiss={() => setShowOfflineToast(false)}
            message="No hay conexión a internet. Algunas funciones pueden estar limitadas."
            position="bottom"
            color="warning"
            duration={3000}
            buttons={[
              {
                text: 'OK',
                role: 'cancel',
                handler: () => {
                  setShowOfflineToast(false);
                }
              }
            ]}
          />

          <IonToast
            isOpen={showOnlineToast}
            onDidDismiss={() => setShowOnlineToast(false)}
            message="Conexión a internet restablecida."
            position="bottom"
            color="success"
            duration={3000}
            buttons={[
              {
                text: 'OK',
                role: 'cancel',
                handler: () => {
                  setShowOnlineToast(false);
                }
              }
            ]}
          />

          <IonToast
            isOpen={showCachedDataToast}
            onDidDismiss={() => setShowCachedDataToast(false)}
            message={`Usando datos almacenados en caché (${cachedDataInfo.age} horas de antigüedad). Actualice para obtener datos recientes.`}
            position="bottom"
            color="medium"
            duration={5000}
            buttons={[
              {
                text: 'Actualizar',
                handler: () => {
                  window.location.reload();
                }
              },
              {
                text: 'OK',
                role: 'cancel',
                handler: () => {
                  setShowCachedDataToast(false);
                }
              }
            ]}
          />
        </>
      )}
    </>
  );
};

export default ConnectionStatus;
